import {
  convertProviderToNiceName,
  FACEBOOK,
  FORM_NAME,
  GOOGLE,
  INSTAGRAM,
  LINKEDIN,
  POST_TYPE,
  TWITTER,
} from "../../../socialConstants";
import { validateTime } from "./scheduleTimeValidation"

/**
 * Get Form Name.
 * Getting a form name from the provider and tab.
 *
 * @param provider
 * @param postTypeTab
 * @returns {string|undefined}
 */
export function getFormName(provider, postTypeTab) {
  const niceName = convertProviderToNiceName(provider);
  switch (niceName) {
    case FACEBOOK:
      return FORM_NAME.FacebookForm;
    case INSTAGRAM:
      return FORM_NAME.InstagramForm;
    case TWITTER:
      return FORM_NAME.TwitterForm;
    case LINKEDIN:
      return FORM_NAME.LinkedInForm;
    case GOOGLE:
      const { postTopicType } = postTypeTab;
      if (postTopicType === POST_TYPE.QA) return FORM_NAME.GoogleQAForm;
      else if (postTopicType === POST_TYPE.ALERT)
        return FORM_NAME.GoogleAlertForm;
      else if (postTopicType === POST_TYPE.OFFER)
        return FORM_NAME.GoogleOfferForm;
      else if (postTopicType === POST_TYPE.WHATS_NEW)
        return FORM_NAME.GoogleWhatsNewForm;
      else return undefined;
    default:
      return undefined;
  }
}

/**
 * To get the provider requirements for publishing
 * @param formType - the type of form for the PostEditor.
 */
export function getConfigByType(formType) {
  switch (formType) {
    case FORM_NAME.FacebookForm:
      return facebookConfig; // defaultConfig;
    case FORM_NAME.InstagramForm:
      return instagramConfig();
    case FORM_NAME.TwitterForm:
      return twitterConfig;
    case FORM_NAME.GoogleQAForm:
      return googleQAConfig;
    case FORM_NAME.GoogleAlertForm:
      return googleAlertConfig;
    case FORM_NAME.GoogleOfferForm:
      return googleOfferConfig;
    case FORM_NAME.GoogleWhatsNewForm:
      return googleWhatsnewConfig;
    case FORM_NAME.LinkedInForm:
      return linkedInConfig;
    default:
      return defaultConfig;
  }
}

const defaultConfig = {
  canPublishImmediateReply: false,
  requireImmediateReply: false,
  createDescription: "Create a Post",
  textPlaceholder: "Enter your post here ...",
  minLength: 2,
  minReplyLength: 3,
  canAttachImages: true,
  canAttachVideos: true,
  canAttachLink: true,
  replyWord: "reply",
  maxAttachments: 4,
  maxNumberOfLinks: 1,
  maxNumberOfImages: 3,
  maxNumberOfVideos: 1,
};

// An alias for readability and symmetry.
const facebookConfig = defaultConfig;

const linkedInConfig = {
  ...defaultConfig,
  canAttachImages: true,
  canAttachVideos: false, // TODO: Implement multi-part uploads to attach videos or figure out the upper bound in size to validate
  canAttachLink: false,
  maxNumberOfImages: 3,
  maxNumberOfVideos: 1,
  canHaveMixedMedia: false,
  requireDescription: true,
};

const defaultGoogleConfig = {
  replyWord: "answer",
  canPublishImmediateReply: false,
  requireImmediateReply: false,
  minReplyLength: undefined,
  maxReplyLength: undefined,
  textPlaceholder: "Update your status",
  minLength: 1,
  maxLength: 1500,
  minSummaryLength: 1,
  maxSummaryLength: 1500,
  minEventTitleLength: 0,
  maxEventTitleLength: 58,
  minCouponLength: 0,
  maxCouponLength: 58,
  requireValidDateRanges: false,
  maxAttachments: 0,
  maxNumberOfImages: 0,
  maxNumberOfVideos: 0,
  maxNumberOfLinks: 0,
  canAttachImages: false,
  disableAttachments: true,
  requireValidUrl: true,
};

const googleQAConfig = {
  ...defaultGoogleConfig,
  canPublishImmediateReply: true,
  requireImmediateReply: true,
  textPlaceholder: "Enter your question here ...",
  minLength: 10,
  maxLength: 4096,
  maxReplyLength: 4096,
};

const googleAlertConfig = defaultGoogleConfig;

const googleOfferConfig = {
  ...defaultGoogleConfig,
  minSummaryLength: 0,
  minEventTitleLength: 1,
  maxEventTitleLength: 58,
  requireValidDateRanges: true,
  disableAttachments: false,
  maxAttachments: 1,
  maxNumberOfImages: 1, // Google says you can upload up to 10 images, but when you do it via API it errors out if > 1
};

const googleWhatsnewConfig = {
  ...defaultGoogleConfig,
  textPlaceholder: "Write your post",
  disableAttachments: false,
  maxAttachments: 1,
  maxNumberOfImages: 1, // Google says you can upload up to 10 images, but when you do it via API it errors out if > 1
};

const twitterConfig = {
  ...defaultConfig,
  maxLength: 280,
};

function instagramConfig() {
  const aspectRatio = {
    min: {
      width: 4,
      height: 5,
    },
    max: {
      width: 1.91,
      height: 1,
    },
    dimensionRequirement: {
      minHeight: 566,
      maxHeight: 3024,
      minWidth: 320,
      maxWidth: 4032,
    },
  };
  const videoAspectRatio = {
    min: {
      width: 4,
      height: 5,
    },
    max: {
      width: 16,
      height: 9,
    },
    dimensionRequirement: {
      maxHeight: 2400,
      maxWidth: 1920,
    },
  };
  return {
    ...defaultConfig,
    canAttachImages: true,
    requireAttachment: true,
    canAttachVideos: true,
    canHaveMixedMedia: false,
    canAttachLink: false,
    minVideoDuration: 3, // 3 Seconds
    maxVideoDuration: 60, // 60 Seconds
    maxNumberOfImages: 1,
    maxNumberOfVideos: 1,
    aspectRatio,
    videoAspectRatio,
    requireDescription: true,
    maxNumberOfHashtags: 30
  };
}
